import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { GridOptions, ColDef, GridApi } from "ag-grid-community";
import { Observable, empty, forkJoin } from 'rxjs';
import { filter, map } from "rxjs/operators";
import { dateComparator } from '../../../../common/components/data-table/comparators/data-table.comparators';
import { DomainDefinitionService } from "../../../../builder/services/domain-definition.service";
import { DomainValue } from "../../../../models/domain.model";
import { FinancialAssuranceService } from '../../../../common/services/financial-assurance.service';
import { CheckboxRendererComponent } from '../../../../common/components/ag-grid/checkbox-renderer.component';
import { ExpiringInsurancePolicy } from '../../../../common/models/financialAssurance.model';
import { GridFormatterService } from '../../../../common/services/grid-formatter.service';
import { ActionCellRendererComponent } from '../../../../common/components/ag-grid/action-cell-renderer.component';
import { IBooleanFilterParams, AgGridBooleanFilterComponent } from '../../../../common/components/ag-grid/filters/boolean-filter.component';
import { OrganizationComplianceDetails } from "../../../../common/models/organization.model";

export class ExpiringInsurancePolicyListing {
    insuranceId: string;
    organizationId: string;
    organizationName: string;
    agency: string;
    agent?: string;
    emailAddress?: string;
    phoneNumber: string;

    agencyId: string;
    limit: number;
    financialInstrumentId?: string;
    effectiveDate: Date;
    expirationDate: Date;
    hasEnvironmentalEndorsement: boolean;
    insurer?: string;
    isActive: boolean;
    policyNumber?: string;
    type?: string;

}

@Component({
    selector: 'internal-upcoming-insurance-expiration-widget',
    templateUrl: './internal-upcoming-insurance-expiration-widget.component.html',
    standalone: false
})
export class InternalUpcomingInsuranceExpirationWidget implements OnInit {

    @Output() companyClicked = new EventEmitter<string>();

    expiringInsurancePolicies: Observable<ExpiringInsurancePolicyListing[]>;
    timeframe: number = 30;



    @Input() complianceData: OrganizationComplianceDetails[];
    @Input() gridAriaLabel: string;


    columnDefs: ColDef[] = [
        {
            headerName: "Company",
            headerTooltip: "Company",
            field: 'organizationName',
            tooltipField: 'organizationName',
            cellRendererSelector: (params) => {
                if (this.isNotHistoricalOrg(params.data)) {
                    return {
                        component: ActionCellRendererComponent,
                        params: {
                            textField: 'organizationName',
                            action: this.showDetail(this),
                        }
                    }
                }
            },
            filter: "agTextColumnFilter",
            filterParams: this.formatterService.defaultTextFilterParams,
        },        
        {
            headerClass: "wrapped",
            headerName: "Owner Type",
            headerTooltip: "Owner Type",
            tooltipValueGetter: this.formatOwnerType(),
            valueGetter: this.formatOwnerType(),
            minWidth: 110
        },
        {
            headerName: "Type",
            headerTooltip: "Type",
            field: 'type',
            tooltipField: 'type',
            maxWidth: 110,
        },
        {
            headerName: 'Environmental Endorsement',
            headerTooltip: 'Environmental Endorsement',
            headerClass: "wrapped",
            cellStyle: { 'white-space': 'normal' },
            tooltipValueGetter: this.formatterService.formatBooleanToYesNo("hasEnvironmentalEndorsement"),
            cellRenderer: CheckboxRendererComponent,
                cellRendererParams: {
                    action: () => { return false; },
                    field: "hasEnvironmentalEndorsement",
                    description: "Environmental Endorsement",
                    isVisible: () => { return true; },
                    isEnabled: () => { return false; },
                    renderAsYesNo: true
            },
            field: "hasEnvironmentalEndorsement",
            filter: AgGridBooleanFilterComponent,
            filterParams: {
                isInServerSideFilterMode: false,
            } as IBooleanFilterParams,
            minWidth: 145,
            maxWidth: 145,
        },
        {
            headerName: "Insurer",
            headerTooltip: "Insurer",
            field: 'insurer',
            tooltipField: 'insurer',
            filter: "agTextColumnFilter",
            filterParams: this.formatterService.defaultTextFilterParams,
        },
        {
            headerName: "Policy #",
            headerTooltip: "Policy #",
            field: 'policyNumber',
            tooltipField: 'policyNumber',
            filter: "agTextColumnFilter",
            filterParams: this.formatterService.defaultTextFilterParams,
        },
        {
            headerName: 'Expires',
            headerTooltip: 'Expires',
            field: 'expirationDate',
            tooltipValueGetter: this.formatterService.formatDate('expirationDate'),
            valueFormatter: this.formatterService.formatDate('expirationDate'),
            comparator: dateComparator,
            filter: "agDateColumnFilter",
            filterParams: this.formatterService.defaultDateFilterParams,
            maxWidth: 120,
        },
        {
            headerName: "Amount",
            headerTooltip: "Amount",
            field: 'limit',
            tooltipField: 'limit',
            filter: "agNumberColumnFilter",
            filterParams: this.formatterService.defaultNumberFilterParams,
            valueFormatter: this.formatterService.formatCurrency('limit'),
            maxWidth: 200,
        }
    ]

    gridOptions: GridOptions;
	gridApi?: GridApi;
    constructor(
        public service: FinancialAssuranceService,
        public domainService: DomainDefinitionService,
        private formatterService: GridFormatterService,
        private router: Router
    ) {

    }


    fetchData(): void {
        let dataObs$ = this.service.getInsurancePoliciesExpiringSoon(this.timeframe);
        let domainObs$ = this.domainService.getValues("INS_TYPE");

        this.expiringInsurancePolicies = forkJoin([dataObs$, domainObs$]).pipe(map(([data, domains]) => {
            return data.map(e => this.projectInstrument(e, domains));
        }));

        if (this.gridApi)
            this.gridApi.sizeColumnsToFit()
    }

    formatOwnerType(): any {
        return (obj) => {
            let returnValue: string = "";
            if (obj.data != null && obj.data["organizationId"] != null && this.complianceData != null) {
                let organizationId = obj.data["organizationId"];
                let compliance = this.complianceData.find(c => c.orgId == organizationId);
                if (compliance != null) {
                    if (compliance.isBrineHauler != null) {
                        if (compliance.isBrineHauler == true) returnValue = "Brine Hauler";
                    }
                    if (compliance.isWellOwner != null) {
                        if (compliance.isWellOwner == true) {
                            if (returnValue.length > 0) {
                                returnValue += ", ";
                            }
                            returnValue += "Well Owner";
                        }
                    }
                }

            }
            if (returnValue.length === 0) returnValue = "N/A";
            return returnValue;
        }
    }

    ngOnInit() {
        this.fetchData();
        this.gridOptions = this.formatterService.BuildDefaultGridOptionsWithColumns(this.columnDefs);
        this.gridOptions.headerHeight = 50;
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        this.router.events.pipe(filter(e => e instanceof NavigationStart)).subscribe((routerEvent: NavigationStart) => {
            this.expiringInsurancePolicies = empty();
        });
    }

    showDetail(component: InternalUpcomingInsuranceExpirationWidget) {
        return (obj) => {
            component.companyClicked.emit(obj.data.organizationId);
        }
    }

    projectInstrument(instrument: ExpiringInsurancePolicy, domains: DomainValue[]): ExpiringInsurancePolicyListing {

        let expiringInsurancePolicy = {
            insuranceId: instrument.insuranceId,
            organizationId: instrument.organizationId,
            organizationName: instrument.organizationName,
            agency: instrument.agency,
            agent: instrument.agent,
            emailAddress: instrument.emailAddress,
            phoneNumber: instrument.phoneNumber,
        } as ExpiringInsurancePolicyListing;

        let type = instrument.policy.type;
        let domainValue = domains.find(d => d.Code.toLocaleLowerCase() === instrument.policy.type.toLocaleLowerCase()
            || d.Code.toLocaleLowerCase().indexOf(instrument.policy.type.toLocaleLowerCase()) !== -1);

        if (domainValue != null) {
            type = domainValue.Definition;
            type = type.replace("Liability", "");
            type = type.replace("(each occurrence)", "");
            type = type.trim();
        }
       

        if (instrument.policy) {
            expiringInsurancePolicy.agencyId = instrument.policy.agencyId;
            expiringInsurancePolicy.limit = instrument.policy.limit;
            expiringInsurancePolicy.financialInstrumentId = instrument.policy.financialInstrumentId;
            expiringInsurancePolicy.effectiveDate = instrument.policy.effectiveDate;
            expiringInsurancePolicy.expirationDate = instrument.policy.expirationDate;
            expiringInsurancePolicy.insurer = instrument.policy.insurer;
            expiringInsurancePolicy.isActive = instrument.policy.isActive;
            expiringInsurancePolicy.hasEnvironmentalEndorsement = instrument.policy.hasEnvironmentalEndorsement;
            expiringInsurancePolicy.policyNumber = instrument.policy.policyNumber;
            expiringInsurancePolicy.type = type;
        }

        return expiringInsurancePolicy;
    }

    resizeColumns(): void {
        setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
    }

    public isNotHistoricalOrg(obj): boolean {
        let isHistorical = false;
        //Any org registered with RICS will have an Id starting with "ON"
        if (obj && obj.organizationId && obj.organizationId[0] != 'O' && ((obj.organizationId.length > 1 && obj.organizationId[1] != 'N') || obj.organizationId.length <= 1)) {
            isHistorical = true;
        }

        return !isHistorical;        
    }
}



